import { useFormikContext } from 'formik';
import moment from 'moment';
import { rgba } from 'polished';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useSearch, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	Avatar,
	Checkbox,
	Empty,
	Spinner,
	tokens,
} from '@treams/treams-ui';

const ROW_HEIGHT = 56;

const Table = styled.div`
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
	flex-direction: column;
	min-height: 0;
	margin: 0 ${tokens.padding.container}px ${tokens.padding.container}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.subtle};
	box-shadow: ${tokens.shadow.xs};
	border-radius: ${tokens.radius.md}px;
	overflow: hidden;
`;

const TableContents = styled.div`
	position: relative;
	flex-grow: 1;
	flex-shrink: 1;
	min-height: 0;
`;

const TableRow = styled(Flex)<{ $header?: boolean; $selected?: boolean; $clickable?: boolean }>`
	flex-direction: row;
	align-items: stretch;
	white-space: nowrap;
	box-sizing: border-box;
	min-height: ${ROW_HEIGHT}px;
	gap: ${tokens.gap.group}px;
	padding: 0 ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};
	color: ${tokens.text.primary};
	background: ${(props) =>
		props.$header
			? tokens.surface.primary
			: props.$selected
				? rgba(props.theme.colors.primary, 0.08)
				: tokens.surface.white};

	${(props) =>
		props.$clickable &&
		`
		cursor: pointer;

		&:hover {
			background: ${props.$selected ? rgba(props.theme.colors.primary, 0.08) : tokens.surface.hover};
		}
	`}
`;

const Sticky = styled(Box)`
	display: table;
	position: sticky;
	top: 0;
	z-index: 1;
`;

const TableCell = styled.div<{
	$small?: boolean;
	$sortable?: boolean;
	$sorted?: boolean;
	$width?: number;
}>`
	padding: 0;
	width: ${(props) => props.$width ?? (props.$small ? 40 : 150)}px;
	flex-shrink: 0;
	flex-grow: 0;
	overflow: hidden;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	color: ${tokens.text.primary};
	${(props) => props.$sortable && `cursor: pointer; user-select: none;`}
	${(props) =>
		props.$sorted &&
		`
		font-weight: ${tokens.typography.weights.medium};
	`}
`;

const HeaderCell = styled(TableCell)`
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.regular};
	letter-spacing: 0.01em;
	line-height: ${tokens.typography.lineHeights.small};
	color: ${tokens.text.secondary};
`;

const SortIndicator = styled.span`
	display: flex;
	flex-shrink: 0;
	color: ${tokens.icon.tertiary};

	svg {
		width: 16px;
		height: 16px;
		display: block;
		color: inherit;
	}
`;

function Result(props: {
	index: number;
	style: any;
	data: {
		linked?: boolean;
		results: any[];
		selection: graphql.User[];
		onChange: (selected: graphql.User[]) => void;
		columns: ('firstName' | 'lastName' | 'username' | 'email' | 'activatedAt' | 'lastLoginAt')[];
	};
}) {
	const history = useHistory();
	const { canViewAndEditAllSettings } = useViewerRoles();

	if (props.index === 0) {
		return null;
	}

	const result = props.data.results[props.index - 1];
	const selected = props.data.selection.some(({ id }) => id === result.id);

	const lastLoginAt = result.lastLoginAt ? moment(result.lastLoginAt).format('LLL') : '-';
	const activatedAt = result.activatedAt ? moment(result.activatedAt).format('L') : '-';

	function toggle() {
		const next = props.data.selection.filter(({ id }) => id !== result.id);

		if (!selected) {
			next.push(result);
		}

		props.data.onChange(next);
	}

	const url = `/settings/employees/${result.id}`;

	return (
		<TableRow
			key={result.id}
			$clickable
			$selected={selected}
			style={{ ...props.style, width: 'auto' }}
			onClick={
				props.data.linked !== false
					? (e) => {
							if (e.metaKey || e.ctrlKey) window.open(url, '_blank');
							else history.push(url);
						}
					: toggle
			}
		>
			{canViewAndEditAllSettings && (
				<TableCell $small>
					<Box onClick={(event) => event.stopPropagation()}>
						<Checkbox mr={0} checked={selected} onClick={toggle} />
					</Box>
				</TableCell>
			)}
			<TableCell $small>
				<Avatar
					size={32}
					user={{
						...result,
						avatar: result.avatar,
					}}
				/>
			</TableCell>
			{props.data.columns.includes('firstName') && <TableCell>{result.firstName}</TableCell>}
			{props.data.columns.includes('lastName') && <TableCell>{result.lastName}</TableCell>}
			{props.data.columns.includes('username') && <TableCell>{result.username}</TableCell>}
			{props.data.columns.includes('email') && <TableCell $width={250}>{result.email}</TableCell>}
			{props.data.columns.includes('activatedAt') && <TableCell>{activatedAt}</TableCell>}
			{props.data.columns.includes('lastLoginAt') && <TableCell>{lastLoginAt}</TableCell>}
		</TableRow>
	);
}

export default function EmployeesList(props: {
	linked?: boolean;
	selection: { id: string; firstName: string; lastName: string }[];
	onChange: (users: { id: string; firstName: string; lastName: string }[]) => void;
	columns?: ('firstName' | 'lastName' | 'username' | 'email' | 'activatedAt' | 'lastLoginAt')[];
}) {
	const formik = useFormikContext<{
		status: graphql.UserStatus[];
		query: string;
		groupIDs: string[];
		cycleTemplateIDs: string[];
		jobTitleIDs: string[];
		isExternal: string;
	}>();
	const { t } = useTranslation();
	const { canViewAndEditAllSettings } = useViewerRoles();

	const filters: string[] = [];
	const roles = useViewerRoles();

	if (formik.values.groupIDs.length > 0) {
		filters.push('(' + formik.values.groupIDs.map((id) => [`groupID:${id}`]).join(' OR ') + ')');
	}

	if (formik.values.cycleTemplateIDs.length > 0) {
		filters.push(
			'(' +
				formik.values.cycleTemplateIDs
					.map((id: string) => (id === '0' ? 'cycles:0' : `cycleTemplateIDs:${id}`))
					.join(' OR ') +
				')',
		);
	}

	if (formik.values.jobTitleIDs.length > 0) {
		filters.push(
			'(' +
				formik.values.jobTitleIDs
					.map((id: string) => (id === '0' ? 'jobs:0' : `jobTitleIDs:${id}`))
					.join(' OR ') +
				')',
		);
	}

	if (formik.values.isExternal) {
		filters.push(formik.values.isExternal);
	}

	const query = graphql.useManagerInboxQuery({
		fetchPolicy: 'network-only',
		variables: { recurse: true },
		skip: roles.isWorkspaceSuperuser || roles.isAdministrator,
	});

	const managees = new Set(query.data?.viewer.managees.map(({ id }) => id) ?? []);

	const { loading, results } = useSearch(formik.values.query, {
		status: formik.values.status,
		filters: filters.join(' AND '),
	});

	const [sortBy, setSortBy] = useState('firstName');
	const [sortDirection, setSortDirection] = useState(1);

	const sortedResults = results
		.filter((result) => {
			if (result.email === 'support@treams.nl') {
				return;
			}

			if (
				roles.isWorkspaceSuperuser ||
				roles.isAdministrator ||
				roles.isHRBusinessPartner ||
				managees.has(result.id)
			) {
				return result;
			}
		})
		.sort((a, b) => {
			if (sortBy === 'lastLoginAt') {
				return sortDirection * Math.sign((a.lastLoginAt ?? 0) - (b.lastLoginAt ?? 0));
			}

			if (sortBy === 'activatedAt') {
				return sortDirection * Math.sign((a.activatedAt ?? 0) - (b.activatedAt ?? 0));
			}

			if (sortBy === 'firstName') {
				const aName = a.firstName + ' ' + a.lastName;
				const bName = b.firstName + ' ' + b.lastName;
				return sortDirection * aName.localeCompare(bName);
			}

			return sortDirection * a[sortBy].localeCompare(b[sortBy]);
		});

	const sort = (attribute: string) => {
		if (sortBy === attribute) {
			setSortDirection(-sortDirection);
		} else {
			setSortBy(attribute);
			setSortDirection(1);
		}
	};

	const columns = props.columns ?? [
		'firstName',
		'lastName',
		'username',
		'email',
		'activatedAt',
		'lastLoginAt',
	];

	const headerCell = (attribute: (typeof columns)[number], label: string, width?: number) => (
		<HeaderCell
			$sortable
			$sorted={sortBy === attribute}
			$width={width}
			onClick={() => sort(attribute)}
		>
			{label}
			{sortBy === attribute && (
				<SortIndicator>{sortDirection === 1 ? <ArrowUpIcon /> : <ArrowDownIcon />}</SortIndicator>
			)}
		</HeaderCell>
	);

	return (
		<>
			{sortedResults.length > 0 ? (
				<Table>
					<TableContents>
						<AutoSizer>
							{({ height, width }) => (
								<FixedSizeList
									width={width}
									height={height}
									itemCount={sortedResults.length + 1}
									itemSize={ROW_HEIGHT}
									itemData={{
										columns,
										results: sortedResults,
										selection: props.selection,
										onChange: props.onChange,
										linked: props.linked,
									}}
									innerElementType={({ children }: PropsWithChildren<{}>) => {
										return (
											<Box style={{ height: sortedResults.length * ROW_HEIGHT + ROW_HEIGHT }}>
												<Sticky>
													<TableRow $header>
														{canViewAndEditAllSettings && (
															<HeaderCell $small>
																<Checkbox
																	mr={0}
																	checked={
																		sortedResults.length > 0 &&
																		props.selection.length === sortedResults.length
																	}
																	onClick={() => {
																		if (props.selection.length === sortedResults.length) {
																			props.onChange([]);
																		} else {
																			props.onChange(sortedResults);
																		}
																	}}
																/>
															</HeaderCell>
														)}
														<HeaderCell $small />
														{columns.includes('firstName') &&
															headerCell('firstName', t('employees.table.header.firstName'))}
														{columns.includes('lastName') &&
															headerCell('lastName', t('employees.table.header.lastName'))}
														{columns.includes('username') &&
															headerCell('username', t('employees.table.header.username'))}
														{columns.includes('email') &&
															headerCell('email', t('employees.table.header.email'), 250)}
														{columns.includes('activatedAt') &&
															headerCell('activatedAt', t('employees.table.header.activatedAt'))}
														{columns.includes('lastLoginAt') &&
															headerCell('lastLoginAt', t('employees.table.header.lastLoginAt'))}
													</TableRow>
												</Sticky>
												{children}
											</Box>
										);
									}}
								>
									{Result}
								</FixedSizeList>
							)}
						</AutoSizer>
					</TableContents>
				</Table>
			) : loading || query.loading ? (
				<Spinner centered />
			) : (
				sortedResults.length === 0 && (
					<Box p={`${tokens.padding.container}px`}>
						<Empty.Card title={t('employees.search.empty')} />
					</Box>
				)
			)}
		</>
	);
}
